import { useState, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { getDealershipCompanies, dealershipSwitch } from "../../lib/queries";
import { useCurrentUser } from "../../../../lib/hooks/useCurrentUser";
import { Company } from "@rumble-it/common-lib/types/models/Company.interface";
import { Select } from "@rumbledirect/ui";

interface DealershipOption {
  value: string;
  label: string;
  id: string; // TODO: Either use IRIs or IDs consistently, this makes matching by ID easier
}

const formatDealershipOptions = (
  dealerships: Company[],
): DealershipOption[] => {
  return dealerships?.map((dealership) => ({
    id: dealership["id"], // TODO: Either use IRIs or IDs consistently, this makes matching by ID easier
    value: dealership["@id"],
    label: dealership["name"],
  }));
};

const SelectDealership = () => {
  const queryClient = useQueryClient();
  const [dealershipCompany, setDealershipCompany] = useState<string | null>(
    null,
  );

  const onChange = (value: string) => {
    if (value) {
      setDealershipCompany(value);
      dealershipSwitchMutation.mutate(value);
    }
  };

  const currentUser = useCurrentUser();

  const dealershipQuery = useQuery({
    queryKey: ["dealershipCompanies"],
    queryFn: getDealershipCompanies,
  });

  const dealershipSwitchMutation = useMutation({
    mutationKey: ["dealershipSwitch"],
    mutationFn: (dealershipIri: string) => {
      if (dealershipIri) {
        return dealershipSwitch(dealershipIri);
      }
      return Promise.resolve(null);
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["currentUser"] }),
  });

  const dealershipOptions: DealershipOption[] = formatDealershipOptions(
    dealershipQuery.data?.member || [],
  );

  const hasDealerships = dealershipOptions.length > 0;

  const affiliateDealershipSwitchingEnabled =
    (currentUser?.role?.dealershipRequired === true &&
      currentUser?.affiliateDealershipSwitchEnabled) ??
    false;

  const hideDealershipSelection =
    !affiliateDealershipSwitchingEnabled || !hasDealerships;

  const currentDealership = currentUser?.role?.dealership;

  useEffect(() => {
    if (dealershipCompany === null && currentDealership && hasDealerships) {
      const dealershipIriFromList = dealershipOptions.find(
        (option) => option.id === currentDealership["id"], // TODO: Either use IRIs or IDs consistently, this makes matching easier
      );
      setDealershipCompany(dealershipIriFromList?.value || null);
    }
  }, [dealershipCompany, currentDealership, dealershipOptions]);

  return hideDealershipSelection ? null : (
    <span className="flex flex-col items-center gap-4">
      <p>
        {currentDealership
          ? "Huidige dealerbedrijf: "
          : "Kies een dealerbedrijf"}
      </p>

      <Select
        placeholder="Kies of zoek een dealerbedrijf"
        showSearch
        optionFilterProp="label"
        options={dealershipOptions}
        className="w-64"
        value={dealershipCompany}
        onChange={onChange}
        loading={dealershipSwitchMutation.isPending}
        disabled={dealershipSwitchMutation.isPending}
      />

      {dealershipSwitchMutation.isError ? (
        <p className="text-red-500">
          Er is een fout opgetreden bij het wisselen van dealerbedrijf. Probeer
          het opnieuw. Indien het probleem aanhoudt, neem dan contact op met de
          beheerder.
        </p>
      ) : null}
    </span>
  );
};

export default SelectDealership;
